import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Modal,
  ProgressBar,
  Spinner,
  Table,
} from "react-bootstrap";

const money = (value) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(
    Number(value || 0),
  );
const date = (value) => {
  if (!value) return "-";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match
    ? `${match[3]}/${match[2]}/${match[1]}`
    : new Date(value).toLocaleDateString("es-AR");
};
const pct = (value) =>
  `${Number(value || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const status = (value) => {
  const normalized = String(value || "PENDING").toUpperCase();
  if (normalized === "COMPLETE") return ["Completo", "success"];
  if (normalized === "EMPTY_CONFIRMED") return ["Vacío", "secondary"];
  if (normalized === "RUNNING") return ["Procesando", "info"];
  if (normalized === "PENDING") return ["Pendiente", "warning"];
  return ["Error", "danger"];
};

// Canal/instrumento de un pago Clover para el filtro de "Pendiente 18 días". El instrumento
// (prepaga) se mira antes que el canal, igual que en el matcher.
const CLOVER_CHANNELS = [
  ["DEBITO", "Débito"],
  ["PREPAGA", "Prepaga"],
  ["QR", "QR"],
  ["CREDITO", "Crédito"],
  ["SIN_DATO", "Sin dato"],
];
const cloverChannel = (row) => {
  const method = String(row?.paymentMethod || "").toUpperCase();
  const all = `${method} ${String(row?.cardType || row?.cardBrandRaw || "").toUpperCase()}`;
  if (all.includes("PREPA")) return "PREPAGA";
  if (method.includes("QR")) return "QR";
  if (method.includes("CRÉDITO") || method.includes("CREDITO")) return "CREDITO";
  if (method.includes("DÉBITO") || method.includes("DEBITO") || all.includes("DEBIT")) return "DEBITO";
  return "SIN_DATO";
};

const pick = (source, ...keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return null;
};
const onlyDigits = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  return digits.replace(/^0+/, "") || (digits ? "0" : "");
};

// Compara Fiserv contra Clover campo a campo. Acepta las dos formas en que llegan los datos:
// fila del flujo Débito (terminal, amount, coupon...) y resultado del matcher general
// (numeroComercio, totalBruto, numeroCupon...).
function ComparisonTable({ fiserv, clover, rule }) {
  const qr = rule === "QR_RECEIPT";
  const fields = [
    { label: "Terminal", f: pick(fiserv, "terminal", "numeroComercio"), c: pick(clover, "detailTerminal", "terminal"), cmp: (a, b) => onlyDigits(a) === onlyDigits(b) },
    { label: "Fecha", f: pick(fiserv, "date", "fechaOperacion"), c: pick(clover, "operationDate", "date"), show: date, cmp: (a, b) => String(a).slice(0, 10) === String(b).slice(0, 10) },
    { label: "Importe", f: pick(fiserv, "amount", "totalBruto"), c: pick(clover, "amount"), show: money, cmp: (a, b) => Number(a) === Number(b) || Math.trunc(Number(a)) === Number(b), note: (a, b) => (Number(a) !== Number(b) && Math.trunc(Number(a)) === Number(b) ? "Clover trunca centavos" : "") },
    { label: "Last4", f: pick(fiserv, "last4", "numeroTarjetaUltimos4"), c: pick(clover, "cardLast4", "last4"), cmp: (a, b) => onlyDigits(a).slice(-4) === onlyDigits(b).slice(-4), qrExempt: true },
    { label: "Cupón / recibo", f: pick(fiserv, "coupon", "numeroCupon"), c: pick(clover, "receiptNumber", "receipt"), cmp: (a, b) => onlyDigits(a) === onlyDigits(b) },
    { label: "Lote", f: pick(fiserv, "batch", "numeroLote"), c: pick(clover, "batchNumber", "batch"), cmp: (a, b) => onlyDigits(a) === onlyDigits(b), qrExempt: true },
    { label: "Autorización", f: pick(fiserv, "numeroAutorizacion"), c: pick(clover, "authorizationCode", "authCode"), info: true },
    { label: "Producto / medio", f: pick(fiserv, "family", "tarjeta"), c: [pick(clover, "paymentMethod"), pick(clover, "cardBrandRaw", "cardType")].filter(Boolean).join(" · ") || null, info: true },
    { label: "Comercio Clover", f: null, c: [pick(clover, "commerceName"), pick(clover, "merchantId")].filter(Boolean).join(" · ") || null, info: true },
    { label: "PaymentId", f: null, c: pick(clover, "paymentId"), info: true },
  ];
  const evaluated = fields.map((field) => {
    if (field.info) return { ...field, state: "info" };
    if (field.f === null || field.c === null) return { ...field, state: "na" };
    if (field.cmp(field.f, field.c)) return { ...field, state: "ok", detail: field.note ? field.note(field.f, field.c) : "" };
    if (qr && field.qrExempt) return { ...field, state: "exempt", detail: "No comparable en QR: la billetera tokeniza la tarjeta y usa otro lote" };
    return { ...field, state: "diff" };
  });
  const identity = evaluated.filter((field) => !field.info);
  const matched = identity.filter((field) => field.state === "ok").length;
  const badge = { ok: ["Coincide", "success"], diff: ["Distinto", "danger"], na: ["Sin dato", "secondary"], exempt: ["No aplica", "warning"], info: ["", ""] };
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
        {rule ? <Badge bg="dark">{rule}</Badge> : null}
        <small className="text-muted">Coinciden <strong>{matched}</strong> de {identity.length} campos de identidad.</small>
      </div>
      <Table bordered size="sm" className="align-middle mb-3">
        <thead>
          <tr><th style={{ width: "18%" }}>Campo</th><th style={{ width: "32%" }}>Fiserv</th><th style={{ width: "32%" }}>Clover</th><th style={{ width: "18%" }}>Resultado</th></tr>
        </thead>
        <tbody>
          {evaluated.map((field) => {
            const [label, variant] = badge[field.state];
            const format = (value) => (value === null ? <span className="text-muted">—</span> : field.show ? field.show(value) : String(value));
            return (
              <tr key={field.label} className={field.state === "diff" ? "table-danger" : field.info ? "text-muted" : undefined}>
                <th className="fw-semibold">{field.label}</th>
                <td>{format(field.f)}</td>
                <td>{format(field.c)}</td>
                <td>{label ? <Badge bg={variant}>{label}</Badge> : null}{field.detail ? <div><small className="text-muted">{field.detail}</small></div> : null}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
}

export default function ConciliacionOperacionesDaily({
  darkMode,
  fechaLiquidacion,
  setFechaLiquidacion,
  archivoZip,
  setArchivoZip,
  analizando,
  handleAnalizarZip,
  operationalRun,
  resultadoConciliacion,
  reportProgress,
  matchingBusy,
  matchingPhase,
  ejecutarMatch,
  resolverReview,
  confirmTerminalMapping,
  consultarCloverNuevosMappings,
  buscarCloverMerchants,
  recalcularDebit,
  resolverDebitRow,
  simularDebitAllClover,
  retryingFailedReports,
  retryFailedReports,
  historialConciliacion,
  cargandoHistorial,
  cargarHistorialConciliacion,
  abrirRunConciliacion,
  abriendoRunId,
  activeReconciliationRunId,
  mappingSummary,
  debit,
  openMappingModal,
  mappingModal,
  setMappingModal,
  mappingCandidates,
  mappingBusy,
  saveMapping,
  mappingQuery,
  setMappingQuery,
  searchMappingCandidates,
  unlinkMapping,
}) {
  const [reportsOpen, setReportsOpen] = useState(true);
  const [tab, setTab] = useState("MATCH");
  const [selectedResult, setSelectedResult] = useState(null);
  const [manualMapping, setManualMapping] = useState(null);
  const [merchantQuery, setMerchantQuery] = useState("");
  const [merchantCandidates, setMerchantCandidates] = useState([]);
  const [debitTab, setDebitTab] = useState("TODAS");
  const [debitQuery, setDebitQuery] = useState("");
  const [debitTerminal, setDebitTerminal] = useState("");
  const [debitFamily, setDebitFamily] = useState("");
  const [debitChannel, setDebitChannel] = useState("TODOS");
  const [debitPageSize, setDebitPageSize] = useState(25);
  const [debitPage, setDebitPage] = useState(1);
  const reports =
    reportProgress?.reports || operationalRun?.clover?.reports || [];
  const total = Number(
    reportProgress?.totalReports ?? reportProgress?.total ?? 0,
  );
  const complete = Number(reportProgress?.complete || 0);
  const empty = Number(reportProgress?.empty || 0);
  const consulted = Number(reportProgress?.consulted ?? complete + empty);
  const running = Number(reportProgress?.running || 0);
  const failed = Number(reportProgress?.failed || 0);
  // `pending` already comes from the run summary. Adding the difference against
  // the detail array counted the same reports twice whenever details were not loaded.
  const pending = Number(reportProgress?.pending || 0);
  const closed = Boolean(reportProgress?.terminal);
  const hasFailures = reportProgress?.hasFailures ?? failed > 0;
  const matchInputError = reportProgress?.matchInputError;
  const finalReady = Boolean(resultadoConciliacion);
  const reportPercent = total
    ? Math.min(100, ((consulted + failed) / total) * 100)
    : 0;
  const summary = resultadoConciliacion?.resumen || {};
  const comparable = Number(
    summary.operacionesComparables ??
      summary.fiservComparable ??
      summary.operacionesFiserv ??
      0,
  );
  const matched = Number(summary.conciliadas ?? summary.match ?? 0);
  const autoMatched = Number(summary.autoMatched ?? matched);
  const manualMatched = Number(summary.manualMatched ?? 0);
  const rate =
    resultadoConciliacion?.run?.matchAutomaticoPorcentaje ??
    (comparable ? (matched / comparable) * 100 : 0);
  const resultCategory = (row) => {
    const value = String(row.estadoActual || row.status || "").toUpperCase();
    return value === "MATCH"
      ? "MATCH"
      : value === "REVIEW" || value === "AMBIGUOUS_REVIEW"
        ? "REVIEW"
        : value === "SOLO_FISERV" || value === "MISSING"
          ? "MISSING"
        : value === "SOLO_CLOVER"
          ? "SOLO_CLOVER"
          : "DIFERENCIA";
  };
  const resultRows = useMemo(
    () =>
      (resultadoConciliacion?.resultados || []).filter(
        (row) => resultCategory(row) === tab,
      ),
    [resultadoConciliacion, tab],
  );
  const mappingItems = useMemo(() => Object.values((mappingSummary?.items || []).reduce((groups, item) => {
    const current = groups[item.terminalFiserv] || { ...item, marcas: [], cantidadOperacionesFiserv: 0, importeFiserv: 0 };
    current.cantidadOperacionesFiserv += Number(item.cantidadOperacionesFiserv || 0);
    current.importeFiserv += Number(item.importeFiserv || 0);
    current.marcas = [...new Set([...(current.marcas || []), ...(item.marcas || [])])];
    if (!current.cloverCandidate && item.cloverCandidate) current.cloverCandidate = item.cloverCandidate;
    current.confirmedDurably = Boolean(current.confirmedDurably || item.confirmedDurably);
    groups[item.terminalFiserv] = current;
    return groups;
  }, {})), [mappingSummary]);
  const debitRows = useMemo(() => debit?.rows || [], [debit?.rows]);
  const debitCloverRows = useMemo(() => debit?.cloverWithoutFiserv || [], [debit?.cloverWithoutFiserv]);
  const debitCounts = useMemo(() => ({
    MATCH: debitRows.filter((row) => row.status === "MATCH").length,
    REVIEW: debitRows.filter((row) => row.status === "REVIEW").length,
    MISSING: debitRows.filter((row) => row.status === "MISSING").length,
    PENDING: debitRows.filter((row) => row.status === "PENDING_CALCULATION").length,
    CLOVER: debitCloverRows.length,
    CERRADAS: debitRows.filter((row) => row.resolution).length,
    PENDIENTES: debitRows.filter((row) => row.status !== "MATCH" && !row.resolution).length,
  }), [debitRows, debitCloverRows]);
  const visibleDebitRows = useMemo(() => {
    const source = debitTab === "CLOVER" || debitTab === "PENDING_18"
      ? debitCloverRows.filter((row) => debitChannel === "TODOS" || cloverChannel(row) === debitChannel)
      : debitTab === "CERRADAS"
        ? debitRows.filter((row) => row.resolution)
        : debitTab === "PENDIENTES"
          ? debitRows.filter((row) => row.status !== "MATCH" && !row.resolution)
          : debitRows.filter((row) => debitTab === "TODAS" || row.status === debitTab);
    const query = debitQuery.trim().toLowerCase();
    return source.filter((row) => (!debitTerminal || String(row.terminal || "").includes(debitTerminal)) && (!debitFamily || String(row.family || row.brand || "") === debitFamily) && (!query || [row.last4, row.coupon, row.batch, row.paymentId].some((value) => String(value || "").toLowerCase().includes(query))));
  }, [debitTab, debitRows, debitCloverRows, debitQuery, debitTerminal, debitFamily, debitChannel]);
  const cloverChannelTotals = useMemo(() => {
    const totals = Object.fromEntries(CLOVER_CHANNELS.map(([key]) => [key, { count: 0, amount: 0 }]));
    for (const row of debitCloverRows) {
      const bucket = totals[cloverChannel(row)];
      bucket.count += 1;
      bucket.amount += Number(row.amount || 0);
    }
    return totals;
  }, [debitCloverRows]);
  const visibleDebitAmount = useMemo(() => visibleDebitRows.reduce((sum, row) => sum + Number(row.amount || 0), 0), [visibleDebitRows]);
  const debitPages = Math.max(1, Math.ceil(visibleDebitRows.length / debitPageSize));
  const pagedDebitRows = visibleDebitRows.slice((Math.min(debitPage, debitPages) - 1) * debitPageSize, Math.min(debitPage, debitPages) * debitPageSize);
  const stepState = (index) => {
    if (index < 2) return "complete";
    if (index === 2) return closed && !hasFailures ? "complete" : "active";
    if (index === 3)
      return matchingBusy ? "active" : finalReady ? "complete" : "pending";
    return finalReady ? "complete" : "pending";
  };
  const steps = [
    "Procesando Fiserv",
    "Identificando comercios",
    "Consultando Clover",
    "Validando operaciones",
    "Conciliación final",
  ];

  return (
    <section
      className={
        darkMode
          ? "container-dark daily-reconciliation-page"
          : "container-light bg-gris daily-reconciliation-page"
      }
    >
      <div className="container py-4">
        <div className="daily-heading">
          <div>
            <div className="daily-eyebrow">OPERACIÓN DIARIA</div>
            <h1>Conciliación de Operaciones</h1>
            <p>
              Validación diaria de liquidaciones Fiserv contra operaciones
              Clover.
            </p>
          </div>
          {finalReady ? (
            <Badge
              pill
              bg={matched === comparable ? "success" : "warning"}
              className="daily-status"
            >
              {matched === comparable
                ? "LISTO PARA LIQUIDAR"
                : "REQUIERE REVISIÓN"}
            </Badge>
          ) : null}
        </div>
        <Card className="daily-card">
          <Card.Body>
            <div className="daily-card-heading">
              <div>
                <h2>Comenzar conciliación</h2>
                <p>
                  Seleccioná la fecha de liquidación y el archivo Fiserv para
                  iniciar.
                </p>
              </div>
              <span className="daily-step-number">01</span>
            </div>
            <Form onSubmit={handleAnalizarZip}>
              <div className="daily-form-grid">
                <Form.Group>
                  <Form.Label>
                    Fecha de liquidación <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={fechaLiquidacion}
                    onChange={(event) =>
                      setFechaLiquidacion(event.target.value)
                    }
                  />
                  <Form.Text>
                    Debe coincidir con la fecha del SAS que vas a conciliar.
                  </Form.Text>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Archivo Fiserv</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".zip"
                    onChange={(event) =>
                      setArchivoZip(event.target.files?.[0] || null)
                    }
                  />
                  <Form.Text>
                    {archivoZip?.name || "Formato ZIP de liquidaciones"}
                  </Form.Text>
                </Form.Group>
                <Button
                  type="submit"
                  variant="success"
                  className="daily-primary-action"
                  disabled={!archivoZip || analizando}
                >
                  {analizando ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Procesando...
                    </>
                  ) : (
                    "Conciliar operaciones"
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
        {!operationalRun && !resultadoConciliacion ? (
          <div className="daily-empty">
            <div className="daily-empty-icon">↑</div>
            <strong>Seleccioná una liquidación Fiserv para comenzar.</strong>
            <span>
              El proceso consultará y validará las operaciones automáticamente.
            </span>
          </div>
        ) : null}
        {operationalRun && !finalReady ? (
          <Card className="daily-card daily-progress-card">
            <Card.Body>
              <div className="daily-card-heading">
                <div>
                  <h2>Procesando conciliación</h2>
                  <p>
                    {matchingBusy
                      ? "Validando las operaciones del lote Clover cerrado."
                      : "Consultando Clover: el progreso se actualiza automáticamente."}
                  </p>
                </div>
                <Badge
                  bg={
                    hasFailures ? "danger" : matchingBusy ? "primary" : "info"
                  }
                >
                  {hasFailures
                    ? "REQUIERE REINTENTO"
                    : matchingBusy
                      ? "VALIDANDO"
                      : "EN PROCESO"}
                </Badge>
              </div>
              <div className="daily-stepper">
                {steps.map((label, index) => {
                  const current = stepState(index);
                  return (
                    <div className={`daily-step is-${current}`} key={label}>
                      <span>{current === "complete" ? "✓" : index + 1}</span>
                      <div>
                        {label}
                        <small>
                          {current === "complete"
                            ? "Completo"
                            : current === "active"
                              ? "En proceso"
                              : "Pendiente"}
                        </small>
                      </div>
                    </div>
                  );
                })}
              </div>
              <ProgressBar
                now={
                  closed && !hasFailures
                    ? 80
                    : Math.max(20, reportPercent * 0.6)
                }
                className="daily-progress"
              />
              <div className="daily-report-heading">
                <div>
                  <div className="daily-eyebrow">CLOVER</div>
                  <h3>
                    {complete} / {total} completados
                  </h3>
                  <span>
                    Reports totales: {total} · Completos: {complete} ·
                    Procesando: {running} · Pendientes: {pending} · Fallidos:{" "}
                    {failed}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setReportsOpen((open) => !open)}
                >
                  {reportsOpen ? "Ocultar detalle" : "Ver detalle"}
                </Button>
              </div>
              <ProgressBar
                now={reportPercent}
                variant={hasFailures ? "danger" : "success"}
                className="daily-report-progress"
              />
              {hasFailures ? (
                <Alert variant="warning" className="daily-retry-alert">
                  <div>
                    <strong>
                      {failed}{" "}
                      {failed === 1
                        ? "comercio requiere"
                        : "comercios requieren"}{" "}
                      reintento
                    </strong>
                    <span>
                      Los reports completos se conservan; se reenvían únicamente
                      los que fallaron.
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="warning"
                    onClick={retryFailedReports}
                    disabled={retryingFailedReports}
                  >
                    {retryingFailedReports ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-1"
                        />
                        Reintentando...
                      </>
                    ) : (
                      "Reintentar fallidos"
                    )}
                  </Button>
                </Alert>
              ) : null}
              {reportsOpen ? (
                <div className="daily-table-wrap daily-report-table">
                  <Table responsive hover className="daily-table">
                    <thead>
                      <tr>
                        <th>Comercio</th>
                        <th>Terminal</th>
                        <th>Fecha</th>
                        <th>Cuenta</th>
                        <th>Estado</th>
                        <th>Pagos</th>
                        <th>Enriquecidos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.length ? (
                        reports.map((report, index) => {
                          const [label, variant] = status(report.status);
                          const enriched = Number(report.enrichedRows || 0);
                          const partial = Number(
                            report.partialEnrichedRows || 0,
                          );
                          return (
                            <tr
                              key={`${report.runId || "report"}-${report.terminalFiserv}-${report.merchantId}-${index}`}
                            >
                              <td>
                                {report.commerceName ||
                                  report.merchantId ||
                                  "-"}
                              </td>
                              <td>{report.terminalFiserv || "-"}</td>
                              <td>{date(report.date)}</td>
                              <td>{report.accountKey || "-"}</td>
                              <td>
                                <Badge bg={variant}>{label}</Badge>
                                {report.error ? (
                                  <small className="daily-report-error">
                                    {report.error}
                                  </small>
                                ) : null}
                              </td>
                              <td>{report.uiPaymentsCount ?? "-"}</td>
                              <td>
                                {enriched}
                                {partial
                                  ? ` + ${partial} parcial${partial === 1 ? "" : "es"}`
                                  : ""}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" className="daily-table-empty">
                            Registrando los reports Clover...
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              ) : null}
              <div className="daily-report-summary">
                <div>
                  <div className="daily-eyebrow">CLOVER</div>
                  <h3>
                    {consulted} / {total} consultados
                  </h3>
                  <span>
                    Total: {total} · Con datos: {complete} · Vacíos: {empty} ·
                    Errores: {failed} · Procesando: {running} · Pendientes:{" "}
                    {pending}
                  </span>
                  {matchingBusy ? (
                    <small className="d-block mt-1 text-muted">
                      {matchingPhase || "Validando operaciones..."} · Cargando transacciones Clover y ejecutando Matcher V2
                    </small>
                  ) : null}
                </div>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setReportsOpen((open) => !open)}
                    disabled={matchingBusy}
                  >
                    {reportsOpen ? "Ocultar detalle" : "Ver detalle"}
                  </Button>
                  {closed && !hasFailures ? (
                    <Button
                      size="sm"
                      variant="success"
                      onClick={ejecutarMatch}
                      disabled={matchingBusy}
                    >
                      {matchingBusy ? <><Spinner animation="border" size="sm" className="me-1" />Validando operaciones...</> : "Validar operaciones"}
                    </Button>
                  ) : null}
                </div>
              </div>
            </Card.Body>
          </Card>
        ) : null}
        {finalReady ? (
          <>
            <Card className="daily-card">
              <Card.Body>
                <div className="daily-card-heading">
                  <div>
                    <div className="daily-eyebrow">
                      RESULTADO DE CONCILIACIÓN
                    </div>
                    <h2>Resumen de la liquidación</h2>
                    <p>
                      Liquidación del{" "}
                      {date(
                        fechaLiquidacion ||
                          resultadoConciliacion.run?.fechaLiquidacion,
                      )}
                    </p>
                  </div>
                  <span className="daily-run-label">
                    Conciliación #
                    {String(
                      resultadoConciliacion.run?.id ||
                        operationalRun?.reconciliationRunId ||
                        "",
                    ).slice(-6)}
                  </span>
                </div>
                <div className="daily-match-hero">
                  <div>
                    <div className="daily-match-value">{pct(rate)}</div>
                    <strong>Conciliadas automáticamente</strong>
                    <ProgressBar
                      now={Math.min(Number(rate), 100)}
                      variant="success"
                    />
                    <small>
                      {matched} de {comparable} operaciones comparables
                    </small>
                    <small>Auto: {autoMatched} · Manual: {manualMatched} · Total conciliado: {matched}</small>
                  </div>
                  <div className="daily-summary-grid">
                    {[
                      ["Operaciones Fiserv", summary.operacionesFiserv],
                      ["Operaciones comparables", comparable],
                      [
                        "Requieren revisión",
                        summary.matchProbable,
                      ],
                      [
                        "Sin encontrar",
                        summary.soloFiserv,
                      ],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <small>{label}</small>
                        <strong>{value ?? 0}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </Card.Body>
            </Card>
            <Card className="daily-card">
              <Card.Body>
                {resultadoConciliacion?.families?.length ? <div className="daily-table-wrap mb-3"><h5>Diagnóstico por familia</h5><Table size="sm"><thead><tr><th>Familia</th><th>Comparables</th><th>Match</th><th>Review</th><th>Missing</th><th>Match rate</th></tr></thead><tbody>{resultadoConciliacion.families.map((family) => <tr key={family.family}><td>{family.family}</td><td>{family.comparable}</td><td>{family.match}</td><td>{family.review}</td><td>{family.missing}</td><td>{pct(family.matchRate)}</td></tr>)}</tbody></Table></div> : null}
                <div className="daily-tabs">
                  {[
                    ["MATCH", "Conciliadas", summary.conciliadas ?? matched],
                    [
                      "REVIEW",
                      "Revisar",
                      summary.matchProbable,
                    ],
                    [
                      "MISSING",
                      "Sin encontrar",
                      summary.soloFiserv,
                    ],
                  ].map(([key, label, count]) => (
                    <button
                      type="button"
                      className={tab === key ? "active" : ""}
                      onClick={() => setTab(key)}
                      key={key}
                    >
                      {label}
                      <span>{count ?? 0}</span>
                    </button>
                  ))}
                </div>
                <div className="daily-table-wrap">
                  <Table responsive hover className="daily-table">
                    <thead>
                      <tr>
                        {[
                          "Terminal",
                          "Fecha",
                          "Importe",
                          "Estado",
                          "Regla",
                          "PaymentId",
                        ].map((label) => (
                          <th key={label}>{label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {resultRows.length ? (
                        resultRows.map((row, index) => (
                          <tr key={row.id || index}>
                            <td>
                              {row.fiserv?.terminal ||
                                row.fiserv?.terminalFiserv ||
                                "-"}
                            </td>
                            <td>
                              {date(
                                row.fiserv?.fechaOperacion ||
                                  row.fiserv?.operationDate,
                              )}
                            </td>
                            <td>
                              {money(
                                row.fiserv?.totalBruto ?? row.fiserv?.amount,
                              )}
                            </td>
                            <td>
                              <Badge
                                bg={
                                  (row.estadoActual || row.status) === "MATCH"
                                    ? "success"
                                    : "warning"
                                }
                              >
                                {row.estadoActual || row.status || "-"}
                              </Badge>
                            </td>
                            <td>{row.rule || "-"}</td>
                            <td>
                              {row.cloverOperacionId || row.clover?.id || "-"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="daily-table-empty">
                            No hay operaciones para esta vista.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </>
        ) : null}
        {mappingSummary ? (
          <Card className="daily-card daily-mapping-card">
            <Card.Body>
              <div>
                <div className="daily-eyebrow">CONFIGURACIÓN</div>
                <h2>Comercios identificados</h2>
                <p>El mapping se administra por separado del flujo diario.</p>
              </div>
              <div className="daily-mapping-number">
                {mappingSummary.suggested || 0}{" "}
                <span>/ {mappingSummary.total || 0}</span>
                <small className="d-block text-muted">identificados (sugeridos)</small>
              </div>
              <div className="daily-mapping-footer">
                <span>
                  Pendientes: <strong>{mappingSummary.pending || 0}</strong>
                  <span className="ms-3">Guardados globalmente: <strong>{mappingSummary.confirmed || 0}</strong></span>
                </span>
                <Button
                  variant="outline-primary"
                  onClick={() => openMappingModal(mappingSummary.item)}
                >
                  Administrar mappings
                </Button>
              </div>
              {mappingItems.length ? (
                <div className="daily-table-wrap mt-3">
                  <Table size="sm" responsive>
                    <thead><tr><th>Terminal</th><th>Familia</th><th>Operaciones</th><th>Importe</th><th>Estado</th><th>Merchant candidato</th><th>Comercio</th><th>Cuenta</th><th>Confianza</th><th /></tr></thead>
                    <tbody>{mappingItems.map((item) => (
                      <tr key={item.terminalFiserv}>
                        <td>{item.terminalFiserv}</td><td>{(item.marcas || []).join(", ") || "-"}</td><td>{item.cantidadOperacionesFiserv || 0}</td><td>{money(item.importeFiserv)}</td><td>{item.estado || "UNMAPPED"}</td><td>{item.cloverCandidate?.merchantId || "-"}</td><td>{item.cloverCandidate?.commerceName || "-"}</td><td>{item.cloverCandidate?.accountKey || "-"}</td><td>{item.confianza || 0}</td>
                        <td>{item.confirmedDurably ? <Button size="sm" variant="success" disabled>Guardado</Button> : item.cloverCandidate ? <Button size="sm" variant="outline-success" onClick={() => confirmTerminalMapping(item)}>Confirmar</Button> : <Button size="sm" variant="outline-secondary" disabled>Buscar otro</Button>}</td>
                      </tr>
                    ))}</tbody>
                  </Table>
                  <small className="text-muted">Confirmar aplica el mapping una vez por terminal, conserva candidatos existentes y no inicia consultas Clover.</small>
                </div>
              ) : null}
            </Card.Body>
          </Card>
        ) : null}
        {debit ? (
          <Card className="daily-card mt-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div><div className="daily-eyebrow">FLUJO ESPECÍFICO</div><h2>CONCILIACIÓN DÉBITO</h2><p>Universo independiente del matcher general.</p></div>
                <div className="d-flex gap-2"><Button title="Se consultarán únicamente los reports faltantes de los mappings nuevos." variant="success" onClick={consultarCloverNuevosMappings} disabled={!debit.newReportNeeds}>Consultar Clover para nuevos mappings ({debit.newReportNeeds || 0})</Button>{debit.mapped === debit.builderAvailable ? <Button variant="primary" disabled={matchingBusy} onClick={recalcularDebit}>{matchingBusy ? "Calculando conciliación Débito..." : debit.calculated ? "Recalcular conciliación Débito" : "Calcular conciliación Débito"}</Button> : null}<Button variant="outline-primary" disabled={matchingBusy} onClick={simularDebitAllClover}>Simular todas Clover</Button></div>
              </div>
              <div className="daily-summary-grid mt-3">
                <div><small>Golden Fiserv Debit</small><strong>{debit.goldenFiserv}</strong></div><div><small>Disponible Builder</small><strong>{debit.builderAvailable}</strong></div><div><small>Mapeado</small><strong>{debit.mapped} / {debit.builderAvailable}</strong></div><div><small>Sin mapping</small><strong>{debit.unmapped}</strong></div><div><small>Recuperadas vs 67</small><strong>{debit.recoveredFromPrevious}</strong></div>
              </div>
              <div className="daily-summary-grid mt-3"><div><small>MATCH Debit</small><strong>{debitCounts.MATCH}</strong></div><div><small>REVIEW Debit</small><strong>{debitCounts.REVIEW}</strong></div><div><small>MISSING Debit</small><strong>{debitCounts.MISSING}</strong></div><div><small>{debit.calculated ? "Clover sin Fiserv" : "Pendiente de cálculo"}</small><strong>{debit.calculated ? debitCounts.CLOVER : debitCounts.PENDING}</strong></div><div><small>Importe Clover sin Fiserv</small><strong>{money(debit.cloverWithoutFiservAmount)}</strong></div></div>
              <small className="d-block mt-2">Mapping coverage: {debit.mapped} / {debit.builderAvailable} · Match rate: {pct(debit.builderAvailable ? debitCounts.MATCH * 100 / debit.builderAvailable : 0)} · Review: {pct(debit.builderAvailable ? debitCounts.REVIEW * 100 / debit.builderAvailable : 0)} · Missing: {pct(debit.builderAvailable ? debitCounts.MISSING * 100 / debit.builderAvailable : 0)}</small>
              {debit.closedCount != null ? <small className="d-block mt-1">Cierre operativo: <strong>{debit.closedCount} / {debit.builderAvailable}</strong> ({pct(debit.builderAvailable ? debit.closedCount * 100 / debit.builderAvailable : 0)}) = MATCH {debitCounts.MATCH} + MANUAL_MATCH {debit.manualMatchCount || 0} + CONFIRMED_MISSING {debit.confirmedMissingCount || 0}. <em>El cierre manual no es evidencia de conciliación; el match rate es {pct(debit.builderAvailable ? debitCounts.MATCH * 100 / debit.builderAvailable : 0)}.</em></small> : null}
              {debit.contradictedResolutionCount ? <small className="d-block mt-1 text-danger">Atención: {debit.contradictedResolutionCount} operación(es) cerradas a mano que el último recálculo concilió solo. Revisá esas resoluciones.</small> : null}
              {debit.missingByReason && Object.keys(debit.missingByReason).length ? <small className="d-block mt-1">Causa de MISSING: {Object.entries(debit.missingByReason).sort((a, b) => b[1] - a[1]).map(([reason, count]) => `${reason}: ${count}`).join(" · ")}</small> : null}
              {debit.mappingHints?.length ? <div className="mt-3"><p className="mb-1"><strong>Mappings incompletos detectados por evidencia Clover ({debit.mappingHints.length})</strong> · Clover informa estas terminales bajo un comercio que no está en su mapping.</p><Table size="sm" responsive><thead><tr><th>Terminal</th><th>Filas sin match</th><th>Merchant que la reporta</th><th>Cuenta</th><th>Comercio</th><th>Pagos Clover</th><th>Mapeada hoy a</th></tr></thead><tbody>{debit.mappingHints.map((hint) => <tr key={`${hint.terminal}-${hint.merchantId}-${hint.accountKey}`}><td>{hint.terminal}</td><td><strong>{hint.unmatchedRows}</strong></td><td>{hint.merchantId}</td><td>{hint.accountKey}</td><td>{hint.commerceName}</td><td>{hint.cloverPayments}</td><td>{(hint.mappedMerchantIds || []).join(", ") || "-"}</td></tr>)}</tbody></Table></div> : null}
              <p className="mt-3 mb-1">Sin mapping: <strong>{debit.unmapped} operaciones</strong> · Terminales pendientes: <strong>{debit.pendingMappings?.length || 0}</strong>.</p>
              {debit.pendingMappings?.length ? <div className="daily-table-wrap"><Table size="sm" responsive><thead><tr><th>Terminal</th><th>Familias</th><th>Operaciones Débito</th><th>Importe Débito</th><th>Estado</th><th>Merchant</th><th>Cuenta</th><th>Confianza</th><th /></tr></thead><tbody>{debit.pendingMappings.map((item) => <tr key={item.terminal}><td>{item.terminal}</td><td>{(item.familias || []).join(", ")}</td><td>{item.operations}</td><td>{money(item.amount)}</td><td>{item.status}</td><td>{item.candidateMerchant || "-"}</td><td>{item.candidateAccount || "-"}</td><td>{item.confidence}</td><td><Button size="sm" variant="outline-primary" onClick={() => { setManualMapping(item); setMerchantQuery(""); setMerchantCandidates([]); }}>Buscar otro</Button></td></tr>)}</tbody></Table></div> : null}
            </Card.Body>
          </Card>
        ) : null}
        {debit ? <Card className="daily-card mt-3"><Card.Body>
          <h2>Detalle de operaciones Débito</h2>
          <div className="d-flex gap-2 flex-wrap mb-3">{[["TODAS", debitRows.length], ["MATCH", debitCounts.MATCH], ["REVIEW", debitCounts.REVIEW], ["MISSING", debitCounts.MISSING], ["PENDIENTES", debitCounts.PENDIENTES], ["CERRADAS", debitCounts.CERRADAS], ["CLOVER", debitCounts.CLOVER], ["PENDING_18", debitCounts.CLOVER]].map(([key, count]) => <Button key={key} size="sm" variant={debitTab === key ? "success" : "outline-secondary"} onClick={() => { setDebitTab(key); setDebitPage(1); }}>{key === "CLOVER" ? "CLOVER SIN FISERV" : key === "PENDING_18" ? "PENDIENTE 18 DÍAS" : key === "PENDIENTES" ? "SIN RESOLVER" : key === "CERRADAS" ? "CERRADAS A MANO" : key} ({count})</Button>)}</div>
          {debitTab === "CLOVER" || debitTab === "PENDING_18" ? (
            <div className="mb-3">
              <div className="d-flex gap-2 flex-wrap">
                {[["TODOS", "Todos", { count: debitCloverRows.length, amount: debitCloverRows.reduce((sum, row) => sum + Number(row.amount || 0), 0) }], ...CLOVER_CHANNELS.map(([key, label]) => [key, label, cloverChannelTotals[key]])].map(([key, label, total]) => (
                  <Button
                    key={key}
                    size="sm"
                    variant={debitChannel === key ? "primary" : "outline-primary"}
                    disabled={key !== "TODOS" && !total.count}
                    title={key === "CREDITO" ? "El crédito no forma parte del universo Débito: se concilia en su propio flujo, y mostrarlo acá lo haría aparecer como falso sin Fiserv." : undefined}
                    onClick={() => { setDebitChannel(key); setDebitPage(1); }}
                  >
                    {label} ({total.count}){total.count ? <small className="ms-1 opacity-75">· {money(total.amount)}</small> : null}
                  </Button>
                ))}
              </div>
              <small className="text-muted d-block mt-1">Mostrando {visibleDebitRows.length} operaciones · {money(visibleDebitAmount)}</small>
            </div>
          ) : null}
          <div className="d-flex gap-2 flex-wrap mb-3"><Form.Control style={{ maxWidth: 180 }} placeholder="Terminal" value={debitTerminal} onChange={(e) => { setDebitTerminal(e.target.value); setDebitPage(1); }} /><Form.Select style={{ maxWidth: 220 }} value={debitFamily} onChange={(e) => { setDebitFamily(e.target.value); setDebitPage(1); }}><option value="">Todas las familias</option>{[...new Set(debitRows.map((row) => row.family).filter(Boolean))].map((family) => <option key={family}>{family}</option>)}</Form.Select><Form.Control style={{ maxWidth: 250 }} placeholder="Last4, cupón, lote o paymentId" value={debitQuery} onChange={(e) => { setDebitQuery(e.target.value); setDebitPage(1); }} /><Form.Select style={{ maxWidth: 100 }} value={debitPageSize} onChange={(e) => { setDebitPageSize(Number(e.target.value)); setDebitPage(1); }}><option>25</option><option>50</option><option>100</option></Form.Select></div>
          <Table responsive size="sm"><thead><tr><th>Terminal</th><th>Fecha</th><th>Familia</th><th>Importe</th><th>Last4</th><th>Cupón</th><th>Lote</th><th>Estado</th><th>Regla / motivo</th><th>PaymentId</th><th>Merchant</th><th /></tr></thead><tbody>{pagedDebitRows.map((row, index) => <tr key={`${row.paymentId || row.terminal}-${index}`}><td>{row.terminal}</td><td>{date(row.date)}</td><td>{row.family || row.brand}</td><td>{money(row.amount)}</td><td>{row.last4}</td><td>{row.coupon || row.receipt}</td><td>{row.batch}</td><td>{row.status}</td><td>{row.rule || row.debitConfidence || row.status}</td><td>{row.paymentId}</td><td>{row.merchantId}</td><td>{!(debitTab === "CLOVER" || debitTab === "PENDING_18") ? <div className="d-flex gap-1 flex-wrap justify-content-end"><Button size="sm" variant="outline-primary" onClick={() => setSelectedResult({ status: row.status, rule: row.rule, reason: row.reason, fiserv: row, clover: row.clover || (row.paymentId ? { paymentId: row.paymentId, merchantId: row.merchantId, commerceName: row.commerceName } : null), candidates: row.candidates || [] })}>{row.status === "REVIEW" ? "Ver candidatos" : "Ver detalle"}</Button>{resolverDebitRow && row.resolution ? <Button size="sm" variant="outline-secondary" title={`${row.resolution}: ${row.resolutionReason || "-"} (${row.resolvedBy || "-"})`} onClick={() => resolverDebitRow(row, "REOPEN")}>Reabrir</Button> : null}{resolverDebitRow && !row.resolution && row.status === "MISSING" ? <Button size="sm" variant="outline-danger" onClick={() => resolverDebitRow(row, "CONFIRMED_MISSING")}>Cerrar sin correspondencia</Button> : null}</div> : null}</td></tr>)}</tbody></Table>
          <div className="d-flex justify-content-between align-items-center"><small>{visibleDebitRows.length} resultados · página {Math.min(debitPage, debitPages)} de {debitPages}</small><div className="d-flex gap-2"><Button size="sm" disabled={debitPage <= 1} onClick={() => setDebitPage((page) => page - 1)}>Anterior</Button><Button size="sm" disabled={debitPage >= debitPages} onClick={() => setDebitPage((page) => page + 1)}>Siguiente</Button></div></div>
          {!debit.calculated ? <Alert variant="info" className="mt-3 mb-0">Pendiente de cálculo: {debitCounts.PENDING} operaciones Débito. Todavía no son MISSING reales.</Alert> : debitCounts.MATCH + debitCounts.REVIEW + debitCounts.MISSING !== debit.builderAvailable ? <Alert variant="warning" className="mt-3 mb-0">Advertencia técnica: el resultado persistido no cubre el universo Débito completo.</Alert> : null}
        </Card.Body></Card> : null}
        <Modal show={Boolean(manualMapping)} onHide={() => setManualMapping(null)}>
          <Modal.Header closeButton><Modal.Title>Buscar comercio Clover · {manualMapping?.terminal}</Modal.Title></Modal.Header>
          <Modal.Body><Form.Control value={merchantQuery} placeholder="Nombre de comercio o merchantId" onChange={(event) => setMerchantQuery(event.target.value)} />
            <Button className="mt-2" disabled={!merchantQuery.trim()} onClick={async () => setMerchantCandidates(await buscarCloverMerchants(merchantQuery))}>Buscar</Button>
            <div className="mt-3">{merchantCandidates.map((candidate) => <Button key={`${candidate.merchantId}|${candidate.accountKey}`} className="w-100 text-start mb-2" variant="outline-success" onClick={() => { confirmTerminalMapping({ ...manualMapping, cloverCandidate: candidate }); setManualMapping(null); }}><strong>{candidate.commerceName}</strong><br /><small>{candidate.merchantId} · {candidate.accountKey}</small></Button>)}</div>
          </Modal.Body>
        </Modal>
        <Card className="daily-card daily-history-card">
          <Card.Body>
            <div className="daily-card-heading">
              <div>
                <h2>Histórico</h2>
                <p>Conciliaciones ejecutadas recientemente.</p>
              </div>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={cargarHistorialConciliacion}
                disabled={cargandoHistorial}
              >
                {cargandoHistorial ? "Actualizando..." : "Actualizar"}
              </Button>
            </div>
            <div className="daily-table-wrap">
              <Table responsive hover className="daily-table">
                <thead>
                  <tr>
                    {[
                      "Fecha liquidación",
                      "Fecha ejecución",
                      "Estado",
                      "Operaciones",
                      "Match rate",
                      "Importe conciliado",
                      "Revisión",
                      "Usuario",
                      "",
                    ].map((label) => (
                      <th key={label}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {historialConciliacion?.runs?.length ? (
                    historialConciliacion.runs.map((row) => (
                      <tr key={row.id} className={row.id === activeReconciliationRunId ? "table-active" : undefined}>
                        <td>{date(row.fechaLiquidacion)}</td>
                        <td>{date(row.fechaCreacion)}</td>
                        <td>
                          <Badge bg="success">
                            {row.estado || "Procesada"}
                          </Badge>
                        </td>
                        <td>
                          {row.cantidadFiserv ?? row.operacionesFiserv ?? 0}
                        </td>
                        <td>{pct(row.matchAutomaticoPorcentaje)}</td>
                        <td>{money(row.importeConciliado)}</td>
                        <td>{row.requierenRevision ?? row.conflictos ?? 0}</td>
                        <td>{row.usuarioNombre || "-"}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="link"
                            onClick={() => abrirRunConciliacion(row.id)}
                            disabled={Boolean(abriendoRunId)}
                          >
                            {abriendoRunId === row.id ? <><Spinner animation="border" size="sm" className="me-1" />Abriendo…</> : row.id === activeReconciliationRunId ? "Abierto" : "Ver detalle"}
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="daily-table-empty">
                        {historialConciliacion?.error ||
                          "Todavía no hay conciliaciones persistidas."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
        <Modal
          show={Boolean(mappingModal)}
          onHide={() => setMappingModal(null)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Administrar mapping</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted">
              Terminal: <strong>{mappingModal?.terminalFiserv}</strong>
            </p>
            <div className="daily-mapping-search">
              <Form.Control
                value={mappingQuery || ""}
                onChange={(event) => setMappingQuery(event.target.value)}
                placeholder="Buscar comercio Clover por nombre o merchant"
                onKeyDown={(event) =>
                  event.key === "Enter" && searchMappingCandidates()
                }
              />
              <Button onClick={searchMappingCandidates}>Buscar</Button>
            </div>
            <div className="daily-table-wrap">
              <Table size="sm">
                <thead>
                  <tr>
                    <th>Comercio</th>
                    <th>Merchant</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {mappingCandidates?.length ? (
                    mappingCandidates.map((candidate, index) => (
                      <tr key={candidate.merchantId || index}>
                        <td>{candidate.commerceName || "-"}</td>
                        <td>
                          {candidate.merchantId ||
                            candidate.dashboardMerchantId ||
                            "-"}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="success"
                            disabled={mappingBusy}
                            onClick={() => saveMapping(candidate)}
                          >
                            Cambiar
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3">
                        Buscá un comercio para cambiar el mapping.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            {mappingModal?.cloverCandidate ? (
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() => unlinkMapping(mappingModal)}
              >
                Desvincular mapping
              </Button>
            ) : null}
          </Modal.Body>
        </Modal>
        {finalReady ? (
          <Card className="daily-card">
            <Card.Body>
              <div className="daily-card-heading">
                <div>
                  <div className="daily-eyebrow">VALIDACIÓN</div>
                  <h2>Resultados persistidos</h2>
                  <p>
                    Abrí cada resultado para ver los datos Fiserv y Clover
                    usados.
                  </p>
                </div>
              </div>
              <div className="daily-tabs">
                {["MATCH", "REVIEW", "MISSING", "SOLO_CLOVER"].map(
                  (key) => (
                    <button
                      type="button"
                      className={tab === key ? "active" : ""}
                      onClick={() => setTab(key)}
                      key={key}
                    >
                      {key}
                      <span>
                        {
                          (resultadoConciliacion.resultados || []).filter(
                            (row) => resultCategory(row) === key,
                          ).length
                        }
                      </span>
                    </button>
                  ),
                )}
              </div>
              <div className="daily-table-wrap">
                <Table responsive hover className="daily-table">
                  <thead>
                    <tr>
                      <th>Terminal</th>
                      <th>Fecha</th>
                      <th>Importe</th>
                      <th>Resultado</th>
                      <th>Regla</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {resultRows.length ? (
                      resultRows.map((row, index) => (
                        <tr key={index}>
                          <td>
                            {row.fiserv?.terminal ||
                              row.fiserv?.numeroComercio ||
                              "-"}
                          </td>
                          <td>
                            {date(
                              row.fiserv?.fechaOperacion ||
                                row.fiserv?.operationDate,
                            )}
                          </td>
                          <td>
                            {money(
                              row.fiserv?.totalBruto ?? row.fiserv?.amount,
                            )}
                          </td>
                          <td>
                            <Badge
                              bg={
                                resultCategory(row) === "MATCH"
                                  ? "success"
                                  : "warning"
                              }
                            >
                              {resultCategory(row)}
                            </Badge>
                          </td>
                          <td>{row.rule || "-"}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="link"
                              onClick={() => setSelectedResult(row)}
                            >
                              Ver detalle
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="daily-table-empty">
                          No hay resultados en esta categoría.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        ) : null}
        <Modal
          show={Boolean(selectedResult)}
          onHide={() => setSelectedResult(null)}
          size="xl"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Detalle de validación:{" "}
              {selectedResult ? resultCategory(selectedResult) : ""}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ComparisonTable fiserv={selectedResult?.fiserv} clover={selectedResult?.clover} rule={selectedResult?.rule} />
            {selectedResult?.reason || selectedResult?.fiserv?.missingReason ? (
              <p className="small mb-3"><strong>Motivo:</strong> {selectedResult?.reason || selectedResult?.fiserv?.missingReason}</p>
            ) : null}
            <details className="mb-2">
              <summary className="small text-muted">Ver datos crudos (auditoría)</summary>
              <div className="row g-3 mt-1">
                <div className="col-md-6">
                  <h6>Fiserv</h6>
                  <pre className="small bg-light p-3 rounded">{JSON.stringify(selectedResult?.fiserv || null, null, 2)}</pre>
                </div>
                <div className="col-md-6">
                  <h6>Clover</h6>
                  <pre className="small bg-light p-3 rounded">{JSON.stringify(selectedResult?.clover || null, null, 2)}</pre>
                </div>
              </div>
              <pre className="small bg-light p-3 rounded">
                {JSON.stringify({ rule: selectedResult?.rule, evidence: selectedResult?.evidence, contradictions: selectedResult?.contradictions, reason: selectedResult?.reason, candidates: selectedResult?.candidates }, null, 2)}
              </pre>
            </details>
            {resultCategory(selectedResult || {}) === "REVIEW" ? <div className="mt-3"><h6>Candidatos Clover</h6><Table responsive size="sm"><thead><tr><th>PaymentId</th><th>Importe</th><th>Last4</th><th>Receipt</th><th>Batch</th><th>Terminal</th><th>Fecha</th></tr></thead><tbody>{(selectedResult?.candidates || []).map((candidate) => <tr key={candidate.paymentId}><td>{candidate.paymentId}</td><td>{money(candidate.amount)}</td><td>{candidate.cardLast4}</td><td>{candidate.receiptNumber}</td><td>{candidate.batchNumber}</td><td>{candidate.detailTerminal || candidate.terminal}</td><td>{date(candidate.operationDate)}</td></tr>)}</tbody></Table></div> : null}
            {resultCategory(selectedResult || {}) === "REVIEW" ? (
              <div className="d-flex flex-wrap gap-2">
                <Button variant="outline-secondary" onClick={() => resolverReview(selectedResult, "PENDING")}>Dejar pendiente</Button>
                <Button variant="outline-danger" onClick={() => resolverReview(selectedResult, "MANUAL_MISSING")}>Marcar sin correspondencia</Button>
                {(selectedResult?.candidates || []).map((candidate) => <Button key={candidate.paymentId} variant="success" onClick={() => resolverReview(selectedResult, "MANUAL_MATCH", candidate.paymentId)}>Confirmar {candidate.paymentId}</Button>)}
              </div>
            ) : null}
          </Modal.Body>
        </Modal>
        {operationalRun ? (
          <Alert
            variant={matchInputError ? "danger" : "light"}
            className="small"
          >
            <strong>Diagnóstico de run:</strong> CRM Run completo:{" "}
            {operationalRun.reconciliationRunId || "-"} · Clover runs:{" "}
            {(operationalRun.cloverRunIds || []).join(", ") || "-"} · Matcher
            status: {operationalRun.status || "-"}
            {matchInputError ? (
              <>
                <br />
                No se pudo ejecutar la validación de operaciones:{" "}
                {matchInputError}
              </>
            ) : null}
          </Alert>
        ) : null}
      </div>
    </section>
  );
}
